import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';

const ProductDetailLandsat8_9 = () => {
  const history = useHistory();
  const [activeLevel, setActiveLevel] = useState('L1TP');

  const [mapType, setMapType] = useState('satellite');

  const levelInfo = {
    'L1TP': {
      radiometricLevel: 'Top-of-Atmosphere Reflectance',
      geometricProcessing: 'Orthorectified with DEM and Ground Control',
      description:
        'L1TP is the standard Level-1 product for Landsat, providing radiometrically calibrated and terrain-corrected imagery. It includes TOA reflectance and is suitable for general-purpose analysis and visualization.',
    },
    'L2SR': {
      radiometricLevel: 'Surface Reflectance',
      geometricProcessing: 'Orthorectified and Atmospherically Corrected',
      description:
        'L2SR (Level-2 Surface Reflectance) products are derived from L1TP and include atmospheric corrections to estimate reflectance at the Earth’s surface. Useful for time series analysis, vegetation indices, and land change studies.',
    },
    'L2ST': {
      radiometricLevel: 'Surface Temperature',
      geometricProcessing: 'Orthorectified and Atmospheric/Emissivity Corrected',
      description:
        'L2ST (Surface Temperature) products are also derived from L1TP and provide land surface temperature values using TIRS data. Ideal for hydrology, urban heat studies, and thermal anomaly detection.',
    },
  };
  

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{
            color: '#4287f5',
            textTransform: 'none',
            fontWeight: 500
          }}
          onClick={() => history.push('/products')}
        >
          Products &gt; Landsat 8/9
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Product Description
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
          Landsat 8 and 9 extend the legacy of Earth observation, delivering consistent, high-quality multispectral imagery essential for long-term environmental monitoring. Both satellites are equipped with the Operational Land Imager (OLI) and Thermal Infrared Sensor (TIRS), capturing 11 spectral bands (including thermal) at 15 m (panchromatic), 30 m (multispectral), and 100 m (thermal) resolutions.
          <br /><br />
          With a 16-day revisit cycle and global coverage, Landsat imagery supports applications such as agriculture monitoring, water resource management, urban expansion analysis, and forest health assessment. The data is freely available and widely trusted for scientific and operational use.
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4, border: '1px solid #d0d7de', boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Geographical Coverage</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Image Resolution</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Source</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Revisit Frequency</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Data Format</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell align="center">Global</TableCell>
              <TableCell align="center">15 m / 30 m / 100 m</TableCell>
              <TableCell align="center">Landsat 8 / 9</TableCell>
              <TableCell align="center">16-day cycle</TableCell>
              <TableCell align="center">GeoTIFF</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Table sx={{ mt: 4 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Spectral Band</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Wavelength Range</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell>Coastal / Aerosol</TableCell>
              <TableCell>~ 433–453 nm (Band 1)</TableCell>
              <TableCell>Coastal and aerosol studies, water penetration.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Blue</TableCell>
              <TableCell>~ 450–515 nm (Band 2)</TableCell>
              <TableCell>Bathymetric mapping and coastal water mapping.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Green</TableCell>
              <TableCell>~ 525–600 nm (Band 3)</TableCell>
              <TableCell>Vegetation and water body analysis.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Red</TableCell>
              <TableCell>~ 630–680 nm (Band 4)</TableCell>
              <TableCell>Discrimination of vegetation types and soil.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Near-Infrared (NIR)</TableCell>
              <TableCell>~ 845–885 nm (Band 5)</TableCell>
              <TableCell>Vegetation vigor and biomass studies.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Shortwave IR 1 (SWIR 1)</TableCell>
              <TableCell>~ 1560–1660 nm (Band 6)</TableCell>
              <TableCell>Moisture content and snow/cloud discrimination.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Shortwave IR 2 (SWIR 2)</TableCell>
              <TableCell>~ 2100–2300 nm (Band 7)</TableCell>
              <TableCell>Geological and vegetation moisture studies.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Panchromatic</TableCell>
              <TableCell>~ 500–680 nm (Band 8)</TableCell>
              <TableCell>High-resolution grayscale for image sharpening.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Cirrus</TableCell>
              <TableCell>~ 1360–1390 nm (Band 9)</TableCell>
              <TableCell>Detecting high-altitude clouds (cirrus).</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Thermal Infrared 1 (TIRS 1)</TableCell>
              <TableCell>~ 10.6–11.2 µm (Band 10)</TableCell>
              <TableCell>Surface temperature measurements.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Thermal Infrared 2 (TIRS 2)</TableCell>
              <TableCell>~ 11.5–12.5 µm (Band 11)</TableCell>
              <TableCell>Supplementary thermal measurements.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
        <Box sx={{ width: { xs: '100%', md: '50%' }, pr: { md: 2 }, mb: { xs: 3, md: 0 } }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {['L1TP', 'L2SR', 'L2ST'].map(level => (
  <Button
    key={level}
    variant={activeLevel === level ? 'contained' : 'outlined'}
    sx={{
      borderColor: '#ccc',
      color: activeLevel === level ? 'white' : '#1976d2',
      bgcolor: activeLevel === level ? '#1976d2' : 'transparent',
      '&:hover': {
        bgcolor: activeLevel === level ? '#1565c0' : 'rgba(25, 118, 210, 0.04)'
      }
    }}
    onClick={() => setActiveLevel(level)}
  >
    {level}
  </Button>
))}

          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Radiometric processing level:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {levelInfo[activeLevel].radiometricLevel}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Geometric processing:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {levelInfo[activeLevel].geometricProcessing}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {levelInfo[activeLevel].description}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          variant="contained"
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            py: 1,
            px: 4,
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          Order Imagery
        </Button>
      </Box>
    </Box>
  );
};

export default ProductDetailLandsat8_9;
